import {
  CoiExtraction,
  ExtractedPolicy,
  InsuranceCoverage,
  InsuranceImport,
  InsuranceImportJobType,
  InsurancePolicyType
} from "@wingspanhq/wingspan-api-sdk";

import { convertDateStringToUTCMidday } from "../../../utils/dates";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Extraction dates are strings by contract — "what the model read off the
 * document", preserved verbatim when it could not be parsed — so anything
 * unparseable becomes `undefined` rather than an Invalid Date. The policy then
 * fails validation on a missing date instead of crashing the review.
 */
const toCoverageDate = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  // a date-only string is pinned to UTC midday so it renders as the same
  // calendar day in every timezone, as everywhere else in the app
  const parsed = DATE_ONLY_PATTERN.test(value)
    ? convertDateStringToUTCMidday(value)
    : new Date(value);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

/**
 * The certificate-level fields are shared by every policy on the document, so
 * each policy carries its own copy — the same composition v1 did when it built
 * the import payload.
 */
const toCoverage = (
  policy: ExtractedPolicy,
  extraction: CoiExtraction
): InsuranceCoverage => ({
  title: policy.title,
  // The extraction carries the nominally-distinct `NullableInsurancePolicyType`
  // twin of this enum; the members and string values are identical, so the
  // conversion is a nominal one only.
  policyType: (policy.policyType as InsurancePolicyType | null) ?? undefined,
  policyNumber: policy.policyNumber ?? undefined,
  carrier: policy.insurerName ?? undefined,
  coverageForm: policy.coverageForm,
  effectiveDate: toCoverageDate(policy.effectiveDate),
  expirationDate: toCoverageDate(policy.expirationDate),
  limits: policy.limits,
  appliedProvisions: policy.appliedProvisions,
  policySpecificAttributes: policy.policySpecificAttributes,
  territorialLimits: policy.territorialLimits,
  certificateHolder: extraction.certificateHolder,
  namedInsured: extraction.namedInsured,
  descriptionOfOperations: extraction.descriptionOfOperations,
  issuingOrganization: extraction.issuingOrganization,
  cancellationNotice: extraction.cancellationNotice
});

/**
 * The policies a certificate-of-insurance import extracted, each projected onto
 * the `InsuranceCoverage` it becomes once the import is submitted — which is
 * what the review step previews and what the shared coverage renderers
 * (`mapInsuranceCoverageToDataView`, `selectorInsuranceCoverageIssues`) already
 * speak.
 *
 * The extraction hangs off the import's `Extraction` job, so this is empty
 * until that job has run — during upload and processing, and for a document the
 * extractor could make nothing of.
 */
export const selectorInsuranceImportCoverages = (
  insuranceImport: InsuranceImport
): InsuranceCoverage[] => {
  const extraction = (insuranceImport.jobs ?? []).find(
    job =>
      job.type === InsuranceImportJobType.EXTRACTION && !!job.extractedFields
  )?.extractedFields;

  if (!extraction) {
    return [];
  }

  return (extraction.policies ?? []).map(policy =>
    toCoverage(policy, extraction)
  );
};
